/**
 * The playable mystery hand behind the GO page (/this-month), played at
 * /just-play/mystery-hand against the BEN engine, with a leaderboard of the
 * members who made the contract (mysteryHandResults/<id>/entries/<uid>).
 *
 * Same shape as a problem hand (src/components/ProblemHands/problemHandsData.js).
 * One hand is live at a time: to change month, add the new hand here, point
 * MYSTERY_HAND at it and mirror the N/S hands in content-app/lib/thisMonth.js.
 *
 * Hand #2 (Paul, 15 Sep 2026): N/S honours and shape are his; West is his spec
 * (J1098x x KQxx QJx), East got the rest. The spot cards ("x") were filled in
 * by Claude and are listed in the reply so he can change any of them.
 */
import { PROBLEM_HANDS } from "../ProblemHands/problemHandsData";

const RANKS = "AKQJT98765432";

// "S:Q73 H:K85 D:A63 C:K62" -> [{suit, rank}, ...]  ("T" or "10" = ten)
function hand(str) {
  const out = [];
  String(str)
    .trim()
    .split(/\s+/)
    .forEach((chunk) => {
      const [suit, cards] = chunk.split(":");
      (cards.match(/10|[AKQJT98765432]/g) || []).forEach((r) => out.push({ suit, rank: r === "10" ? "T" : r }));
    });
  return out;
}

const HAND_2 = {
  id: "mh2",
  title: "Mystery hand #2",
  deal: {
    hands: {
      // North was given as Qxx Kxx Axx Kxx (12 cards); Paul: "give north an
      // extra diamond" (15 Sep 2026), so the ♦2 moved from East to North.
      N: hand("S:Q73 H:K85 D:A632 C:K62"),
      E: hand("S:6 H:QJT6 D:JT7 C:T9843"),
      S: hand("S:AK52 H:A9742 D:85 C:A7"),
      W: hand("S:JT984 H:3 D:KQ94 C:QJ5"),
    },
    dealer: "S",
    vul: "",
  },
  // 1♥ (S) - 1♠ (W) - 2♠ (N, cue raise) - P - 4♥ - P - P - P
  auction: [
    { seat: "S", call: { level: 1, strain: "H" } },
    { seat: "W", call: { level: 1, strain: "S" } },
    { seat: "N", call: { level: 2, strain: "S" } },
    { seat: "E", call: { kind: "pass" } },
    { seat: "S", call: { level: 4, strain: "H" } },
    { seat: "W", call: { kind: "pass" } },
    { seat: "N", call: { kind: "pass" } },
    { seat: "E", call: { kind: "pass" } },
  ],
  contract: { level: 4, strain: "H", declarer: "S", doubled: 0 },
  lead: { seat: "W", card: { suit: "S", rank: "J" } },
  // Paul's words, verbatim.
  intro: [
    "Explanation of the auction: Your partner's 2♠ bid (bidding their 5 card suit), was a cue raise - showing an 11+ point and 3 card heart support. You sensibly decided to bid game opposite that.",
  ],
  ready: true,
};

export const MYSTERY_HAND = HAND_2;

// This month's other play hands (/this-month, section 2), played at
// /just-play/month-hand/<id>. Same shape and same results collection as the
// mystery hand, so the This month board scores them the same way.
//
// sep26-p1 (Paul, 19 Sep 2026): 4S by South. N/S honours and shape are his
// (Axx J10xx xxx xxx opposite Kxxxx KQ AKx Axx); West is 3343 by his spec and
// leads a diamond (first a heart, changed the same day). The N/S spot cards are Claude's, and E/W were dealt
// at random once within that shape - all listed in the reply for him to change.
// No auction given: PlayTable builds a minimal one for the engine.
const HAND_SEP26_P1 = {
  id: "sep26-p1",
  title: "Game Plan · Hand 1",
  deal: {
    hands: {
      N: hand("S:A63 H:JT74 D:852 C:763"),
      E: hand("S:T8 H:9832 D:T97 C:KQT5"),
      S: hand("S:K9752 H:KQ D:AK4 C:A82"),
      W: hand("S:QJ4 H:A65 D:QJ63 C:J94"),
    },
    dealer: "S",
    vul: "",
  },
  contract: { level: 4, strain: "S", declarer: "S", doubled: 0 },
  // Paul, 19 Sep 2026 (later): "force a diamond lead, not heart". West holds QJ63:
  // the 3, his lowest (the rule he gave for the heart) and also fourth best.
  lead: { seat: "W", card: { suit: "D", rank: "3" } },
  intro: [],
  ready: true,
};

// Problem hands that are ALSO Game Plan play hands (Paul, 20 Sep 2026: "put problem hands
// 12-15 on the game plan page also ... similar to how those other hands are played"). Same
// deal, same id, played in the Game Plan panel and scored from mysteryHandResults/<id>.
// Playing one from the Problem Hands list scores it too (ProblemHandPlay records the result).
// KEEP IN STEP with `playHands` in content-app/lib/thisMonth.js.
// Paul, 25 Sep 2026: problem hands 16-20 added the same way ("put them on game plan, award 3 points each").
export const GAME_PLAN_PROBLEM_IDS = ["ph12", "ph13", "ph14", "ph15", "ph16", "ph17", "ph18", "ph19", "ph20", "ph21", "ph22"];
const PLACEHOLDER_INTRO = /^explanation to come/i;
const fromProblem = (p) => ({
  ...p,
  problemId: p.id,
  intro: (p.intro || []).filter((t) => !PLACEHOLDER_INTRO.test(String(t).trim())),
  ready: true,
});

// "Low towards honors" (Dummy Play Secrets Part 14, Paul, 24 Sep 2026): the article's playable
// hands, opened in a panel on the article page and scored on the Game Plan ladder
// (`articleExercises` in content-app/lib/thisMonth.js - KEEP THE IDS IN STEP). N/S honours,
// shape and West's hand are Paul's; the spot cards and East are Claude's, listed in the reply.
// lth-4 is Mystery hand #2 again under its own id: Paul wants it to score in the article AND
// on Game Plan ("3 points if you do it in this article, and 3 points if you do it in Game Plan").
const LTH_CLOSE = "Back to the article";
const HAND_LTH_1 = {
  id: "lth-1",
  title: "Low towards honors · Hand 1",
  deal: {
    hands: {
      N: hand("S:KQ H:654 D:A54 C:JT987"),
      E: hand("S:982 H:AT98 D:K32 C:AK2"),
      S: hand("S:A76543 H:KQJ2 D:76 C:3"),
      W: hand("S:JT H:73 D:QJT98 C:Q654"),
    },
    dealer: "S",
    vul: "",
  },
  contract: { level: 4, strain: "S", declarer: "S", doubled: 0 },
  // Paul (24 Sep 2026): E/W swapped, "make the lead a low club to the King then a spade
  // switch". West leads the ♣4, East wins the King and switches to the ♠2. Dummy's and South's
  // entries are placeholders (the reader clicks those); East's cards are scripted.
  lead: { seat: "W", card: { suit: "C", rank: "4" } },
  forcedPlays: [
    { seat: "N", card: { suit: "C", rank: "7" } },
    { seat: "E", card: { suit: "C", rank: "K" } },
    { seat: "S", card: { suit: "C", rank: "3" } },
    { seat: "E", card: { suit: "S", rank: "2" } },
  ],
  // Paul (24 Sep 2026): "when hearts are played from the north hand, east plays low the first time".
  playRules: [{ seat: "E", ledBy: "N", suit: "H", play: "low", once: true }],
  intro: [],
  closeLabel: LTH_CLOSE,
  ready: true,
};
const HAND_LTH_2 = {
  id: "lth-2",
  title: "Low towards honors · Hand 2",
  deal: {
    hands: {
      N: hand("S:853 H:AK2 D:543 C:KQ52"),
      E: hand("S:962 H:QT95 D:QJ C:T976"),
      S: hand("S:AK4 H:876 D:AK62 C:J83"),
      W: hand("S:QJT7 H:J43 D:T987 C:A4"),
    },
    dealer: "S",
    vul: "",
  },
  contract: { level: 3, strain: "N", declarer: "S", doubled: 0 },
  lead: { seat: "W", card: { suit: "S", rank: "Q" } },
  intro: [],
  closeLabel: LTH_CLOSE,
  ready: true,
};
const HAND_LTH_4 = {
  ...HAND_2,
  id: "lth-4",
  // The article labels this one "Hand 4" (Hand 3 is the single-suit question, lth-3). It was titled
  // "Hand 3" here until 29 Sep 2026, so the table did not match the article.
  title: "Low towards honors · Hand 4",
  intro: [],
  closeLabel: LTH_CLOSE,
};

export const MONTH_HANDS = {
  [HAND_SEP26_P1.id]: HAND_SEP26_P1,
  [HAND_LTH_1.id]: HAND_LTH_1,
  [HAND_LTH_2.id]: HAND_LTH_2,
  [HAND_LTH_4.id]: HAND_LTH_4,
  ...Object.fromEntries(PROBLEM_HANDS.filter((p) => GAME_PLAN_PROBLEM_IDS.includes(p.id)).map((p) => [p.id, fromProblem(p)])),
};

/** 52 distinct cards, 13 a seat - the same check the article payloads get. */
export function dealProblems(deal) {
  const problems = [];
  const seen = new Map();
  ["N", "E", "S", "W"].forEach((seat) => {
    const cards = deal.hands[seat] || [];
    if (cards.length !== 13) problems.push(`${seat} holds ${cards.length} cards`);
    cards.forEach((c) => {
      const key = c.suit + c.rank;
      if (!RANKS.includes(c.rank) || !"SHDC".includes(c.suit)) problems.push(`${seat}: bad card ${key}`);
      if (seen.has(key)) problems.push(`${key} is in both ${seen.get(key)} and ${seat}`);
      seen.set(key, seat);
    });
  });
  return problems;
}

export function mysteryHandReady(h = MYSTERY_HAND) {
  return h.ready === true && dealProblems(h.deal).length === 0;
}
